/**
 * Test Case Service
 * Handles test case-related database operations
 */

import { BaseService } from './BaseService.js'
import { COLLECTIONS, SCHEMAS } from '../config.js'

export class TestCaseService extends BaseService {
  constructor() {
    super(COLLECTIONS.TEST_CASES)
  }

  /**
   * Get a test case by ID with field mapping
   * @param {string} id - Test case ID
   * @returns {Promise<Object|null>} Test case or null if not found
   */
  async getById(id) {
    const testCase = await super.getById(id)
    if (!testCase) return null
    
    // Map database field names to UI field names
    return {
      ...testCase,
      folderId: testCase.folder_id || testCase.folderId, // Use folder_id if available, fallback to folderId
      projectId: testCase.project_id
    }
  }

  /**
   * Find test cases by field value with field mapping
   * @param {string} field - Field name
   * @param {*} value - Field value
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Array of matching test cases
   */
  async findBy(field, value, options = {}) {
    const testCases = await super.findBy(field, value, options)
    // Map database field names to UI field names
    return testCases.map(testCase => ({
      ...testCase,
      folderId: testCase.folder_id || testCase.folderId, // Use folder_id if available, fallback to folderId
      projectId: testCase.project_id
    }))
  }

  /**
   * Get the next available test case ID
   * @returns {Promise<string>} Next test case ID (e.g., TC00001)
   */
  async getNextTestCaseId() {
    try {
      // Get all test cases sorted by creation date
      const allTestCases = await this.getAll()
      
      // Extract existing test case IDs and find the highest number
      const existingIds = allTestCases
        .map(tc => tc.testCaseId)
        .filter(id => id && id.startsWith('TC'))
        .map(id => {
          const match = id.match(/^TC(\d+)$/)
          return match ? parseInt(match[1], 10) : 0
        })
        .filter(num => !isNaN(num))
      
      // Find the next available ID
      let nextId = 1
      if (existingIds.length > 0) {
        const maxId = Math.max(...existingIds)
        nextId = maxId + 1
      }
      
      // Format as TC00001, TC00002, etc.
      return `TC${nextId.toString().padStart(5, '0')}`
    } catch (error) {
      console.error('Error getting next test case ID:', error)
      // Fallback to timestamp-based ID if there's an error
      return `TC${Date.now().toString().slice(-5)}`
    }
  }

  /**
   * Get multiple next available test case IDs
   * This is useful for bulk operations to avoid race conditions
   * @param {number} count - Number of IDs to generate
   * @returns {Promise<string[]>} Array of next test case IDs (e.g., ['TC00001', 'TC00002', 'TC00003'])
   */
  async getNextTestCaseIds(count) {
    try {
      if (!count || count < 1) {
        return []
      }

      // Get all test cases sorted by creation date
      const allTestCases = await this.getAll()
      
      // Extract existing test case IDs and find the highest number
      const existingIds = allTestCases
        .map(tc => tc.testCaseId)
        .filter(id => id && id.startsWith('TC'))
        .map(id => {
          const match = id.match(/^TC(\d+)$/)
          return match ? parseInt(match[1], 10) : 0
        })
        .filter(num => !isNaN(num))
      
      // Find the starting ID
      let startId = 1
      if (existingIds.length > 0) {
        const maxId = Math.max(...existingIds)
        startId = maxId + 1
      }
      
      // Generate sequential IDs
      const ids = []
      for (let i = 0; i < count; i++) {
        const idNum = startId + i
        ids.push(`TC${idNum.toString().padStart(5, '0')}`)
      }
      
      return ids
    } catch (error) {
      console.error('Error getting next test case IDs:', error)
      // Fallback to timestamp-based IDs if there's an error
      return Array.from({ length: count }, (_, i) => 
        `TC${(Date.now() + i).toString().slice(-5)}`
      )
    }
  }

  /**
   * Create a new test case
   * @param {Object} testCaseData - Test case data
   * @returns {Promise<Object>} Created test case
   */
  async createTestCase(testCaseData) {
    // Generate test case ID if not provided
    // For cloned test cases, ensure we always have a new testCaseId (should be set by caller, but double-check)
    if (!testCaseData.testCaseId) {
      testCaseData.testCaseId = await this.getNextTestCaseId()
    }
    
    // Additional safeguard: If this is a cloned test case, ensure testCaseId is set and valid
    // (The caller should have already set a new testCaseId, but this ensures it's not missing)
    if (testCaseData.cloned_from && !testCaseData.testCaseId) {
      testCaseData.testCaseId = await this.getNextTestCaseId()
    }

    // Set default values if not provided
    if (!testCaseData.status) {
      testCaseData.status = 'active'
    }
    if (!testCaseData.type) {
      testCaseData.type = 'functional'
    }
    if (!testCaseData.priority) {
      testCaseData.priority = 'medium'
    }
    if (!testCaseData.automation_status) {
      testCaseData.automation_status = 'manual'
    }
    if (!testCaseData.steps) {
      testCaseData.steps = []
    }
    if (!testCaseData.tags) {
      testCaseData.tags = []
    }
    if (!testCaseData.case_type) {
      testCaseData.case_type = 'sanity'
    }
    if (!testCaseData.testRunHistory) {
      testCaseData.testRunHistory = []
    }

    // Set required timestamp fields
    const now = new Date()
    if (!testCaseData.created_at) {
      testCaseData.created_at = now
    }
    if (!testCaseData.updated_at) {
      testCaseData.updated_at = now
    }

    // Initialize activities with creation activity
    if (!testCaseData.activities) {
      testCaseData.activities = []
    }
    
    const userId = testCaseData.created_by || 'system'
    const userName = testCaseData.created_by_name || 'System'
    
    // Check if this is a cloned test case
    if (testCaseData.cloned_from && testCaseData.cloned_from_name) {
      testCaseData.activities.push({
        id: Date.now() + Math.random(),
        type: 'created',
        title: 'Test Case Cloned',
        description: `created this test case by cloning from "${testCaseData.cloned_from_name}"`,
        user_id: userId,
        user_name: userName,
        timestamp: now,
        details: {
          testCaseId: testCaseData.testCaseId,
          name: testCaseData.name,
          clonedFrom: testCaseData.cloned_from,
          clonedFromName: testCaseData.cloned_from_name
        }
      })
    } else {
      testCaseData.activities.push({
        id: Date.now() + Math.random(),
        type: 'created',
        title: 'Test Case Created',
        description: `created this test case`,
        user_id: userId,
        user_name: userName,
        timestamp: now,
        details: {
          testCaseId: testCaseData.testCaseId,
          name: testCaseData.name
        }
      })
    }

    // Map folderId to folder_id for database consistency
    if (testCaseData.folderId !== undefined && testCaseData.folder_id === undefined) {
      testCaseData.folder_id = testCaseData.folderId
    }


    const validation = this.validateData(testCaseData, SCHEMAS[COLLECTIONS.TEST_CASES])
    if (!validation.isValid) {
      console.error('❌ Validation failed:', validation.errors)
      throw new Error(`Validation failed: ${validation.errors.join(', ')}`)
    }

    const result = await this.create(validation.data)
    return result
  }

  /**
   * Update test case
   * @param {string} testCaseId - Test case ID
   * @param {Object} updateData - Update data
   * @returns {Promise<Object>} Updated test case
   */
  async updateTestCase(testCaseId, updateData) {
    // Get the existing test case to track changes
    const existingTestCase = await this.getById(testCaseId)
    if (!existingTestCase) {
      throw new Error('Test case not found')
    }

    // For updates, we don't need to validate required fields that already exist
    // Only validate the fields that are being updated
    const updateSchema = {
      name: { type: 'string' },
      description: { type: 'string' },
      project_id: { type: 'string' },
      folderId: { type: 'string' },
      type: { type: 'string', enum: ['functional', 'non-functional', 'api', 'ui', 'performance', 'security'] },
      case_type: { type: 'string', enum: ['sanity', 'smoke', 'regression'] },
      priority: { type: 'string', enum: ['critical', 'high', 'medium', 'low'] },
      automation_status: { type: 'string', enum: ['automated', 'manual', 'semi-automated'] },
      status: { type: 'string', enum: ['active', 'draft', 'deprecated'] },
      steps: { type: 'array' },
      expected_result: { type: 'string' },
      prerequisites: { type: 'string' },
      testData: { type: 'string' },
      tags: { type: 'array' },
      estimated_time: { type: 'number' },
      apiConfig: { type: 'object' },
      assigned_to: { type: 'string' },
      updated_by: { type: 'string' },
      updated_at: { type: 'datetime' }
    }
    
    const validation = this.validateData(updateData, updateSchema)
    if (!validation.isValid) {
      throw new Error(`Validation failed: ${validation.errors.join(', ')}`)
    }

    // Map folderId to folder_id for database consistency (same as createTestCase)
    if (updateData.folderId !== undefined && updateData.folder_id === undefined) {
      updateData.folder_id = updateData.folderId
    }

    // Track field changes for activity log
    const activities = existingTestCase.activities || []
    const userId = updateData.updated_by || 'system'
    const userName = updateData.updated_by_name || 'System'
    const now = new Date()

    // Helper to format field names for display
    const formatFieldName = (field) => {
      return field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    }

    // Helper to format values for display
    const formatValue = (value, field) => {
      if (value === null || value === undefined || value === '') return 'Not set'
      if (typeof value === 'object' && !Array.isArray(value)) return JSON.stringify(value)
      if (Array.isArray(value)) return value.join(', ') || 'None'
      
      // Format specific enum fields
      if (field === 'priority' || field === 'severity') {
        return String(value).charAt(0).toUpperCase() + String(value).slice(1)
      }
      if (field === 'status' || field === 'type' || field === 'case_type' || field === 'automation_status') {
        return String(value).split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
      }
      
      return String(value)
    }

    // Track changes for specific fields
    const fieldsToTrack = ['name', 'description', 'priority', 'status', 'type', 'case_type', 
                          'automation_status', 'estimated_time', 'prerequisites', 'testData', 
                          'assigned_to', 'folderId']

    fieldsToTrack.forEach(field => {
      if (updateData[field] !== undefined && existingTestCase[field] !== updateData[field]) {
        const oldValue = formatValue(existingTestCase[field], field)
        const newValue = formatValue(updateData[field], field)
        
        activities.push({
          id: Date.now() + Math.random(),
          type: 'field_updated',
          title: 'Field Updated',
          description: `${formatFieldName(field)} changed from "${oldValue}" to "${newValue}"`,
          user_id: userId,
          user_name: userName,
          timestamp: now,
          details: {
            field: field,
            oldValue: existingTestCase[field],
            newValue: updateData[field]
          }
        })
      }
    })

    // Track steps changes
    if (updateData.steps !== undefined) {
      const oldStepsCount = (existingTestCase.steps || []).length
      const newStepsCount = (updateData.steps || []).length
      
      if (oldStepsCount !== newStepsCount) {
        activities.push({
          id: Date.now() + Math.random(),
          type: 'field_updated',
          title: 'Test Steps Updated',
          description: `Test steps count changed from ${oldStepsCount} to ${newStepsCount}`,
          user_id: userId,
          user_name: userName,
          timestamp: now,
          details: {
            field: 'steps',
            oldValue: oldStepsCount,
            newValue: newStepsCount
          }
        })
      }
    }

    // Track API config changes
    if (updateData.apiConfig !== undefined) {
      const oldConfig = existingTestCase.apiConfig || {}
      const newConfig = updateData.apiConfig || {}
      
      // Check each API config field
      const apiFields = ['method', 'url', 'headers', 'body', 'expectedResponse']
      apiFields.forEach(field => {
        if (oldConfig[field] !== newConfig[field]) {
          const oldValue = oldConfig[field] || 'Not set'
          const newValue = newConfig[field] || 'Not set'
          
          // Truncate long values for activity description
          const truncateValue = (val) => {
            if (val === 'Not set') return val
            const str = String(val)
            return str.length > 50 ? str.substring(0, 50) + '...' : str
          }
          
          activities.push({
            id: Date.now() + Math.random(),
            type: 'field_updated',
            title: 'API Config Updated',
            description: `API ${formatFieldName(field)} changed from "${truncateValue(oldValue)}" to "${truncateValue(newValue)}"`,
            user_id: userId,
            user_name: userName,
            timestamp: now,
            details: {
              field: `apiConfig.${field}`,
              oldValue: oldConfig[field],
              newValue: newConfig[field]
            }
          })
        }
      })
    }

    // Add activities to update data
    updateData.activities = activities

    return await this.update(testCaseId, updateData)
  }

  /**
   * Get test cases by project
   * @param {string} projectId - Project ID
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Array of test cases
   */
  async getByProject(projectId, options = {}) {
    const testCases = await this.findBy('project_id', projectId, options)
    
    // Filter out deleted test cases and map database field names to UI field names
    const filteredTestCases = testCases.filter(testCase => {
      const isDeleted = testCase.is_deleted === true
      return !isDeleted
    })
    
    
    return filteredTestCases.map(testCase => {
      // Ensure id field is preserved from MongoDB
      const documentId = testCase.id || (testCase._id && typeof testCase._id === 'object' && testCase._id.$oid ? testCase._id.$oid : String(testCase._id || '')) || testCase.Id
      
      return {
        ...testCase,
        id: documentId, // Explicitly set id field to ensure it's always returned
        folderId: testCase.folder_id || testCase.folderId, // Use folder_id if available, fallback to folderId
        projectId: testCase.project_id
      }
    })
  }

  /**
   * Get test cases by folder
   * @param {string} folderId - Folder ID
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Array of test cases
   */
  async getByFolder(folderId, options = {}) {
    return await this.findBy('folderId', folderId, options)
  }

  /**
   * Get test cases by status
   * @param {string} status - Test case status
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Array of test cases
   */
  async getByStatus(status, options = {}) {
    return await this.findBy('status', status, options)
  }

  /**
   * Get test cases by type
   * @param {string} type - Test case type
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Array of test cases
   */
  async getByType(type, options = {}) {
    return await this.findBy('type', type, options)
  }

  /**
   * Get test cases by priority
   * @param {string} priority - Test case priority
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Array of test cases
   */
  async getByPriority(priority, options = {}) {
    return await this.findBy('priority', priority, options)
  }

  /**
   * Get test cases by assigned user
   * @param {string} userId - User ID
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Array of test cases
   */
  async getByAssignedUser(userId, options = {}) {
    return await this.findBy('assigned_to', userId, options)
  }

  /**
   * Get test cases by creator
   * @param {string} userId - User ID
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Array of test cases
   */
  async getByCreator(userId, options = {}) {
    return await this.findBy('created_by', userId, options)
  }

  /**
   * Get test cases by automation status
   * @param {string} automationStatus - Automation status
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Array of test cases
   */
  async getByAutomationStatus(automationStatus, options = {}) {
    return await this.findBy('automation_status', automationStatus, options)
  }

  /**
   * Search test cases
   * @param {string} searchTerm - Search term
   * @param {Object} options - Search options
   * @returns {Promise<Array>} Array of matching test cases
   */
  async searchTestCases(searchTerm, options = {}) {
    const searchOptions = {
      fields: ['name', 'description', 'expected_result'],
      ...options
    }
    return await this.search(searchTerm, searchOptions)
  }

  /**
   * Get test cases by tags
   * @param {Array} tags - Array of tags
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Array of test cases
   */
  async getByTags(tags, options = {}) {
    const conditions = tags.map(tag => this._where('tags', 'array_contains', tag))
    return await this.findWhere(conditions, options)
  }

  /**
   * Add tag to test case
   * @param {string} testCaseId - Test case ID
   * @param {string} tag - Tag to add
   * @returns {Promise<Object>} Updated test case
   */
  async addTag(testCaseId, tag) {
    const testCase = await this.getById(testCaseId)
    if (!testCase) {
      throw new Error('Test case not found')
    }

    if (!testCase.tags.includes(tag)) {
      testCase.tags.push(tag)
      return await this.update(testCaseId, { tags: testCase.tags })
    }

    return testCase
  }

  /**
   * Remove tag from test case
   * @param {string} testCaseId - Test case ID
   * @param {string} tag - Tag to remove
   * @returns {Promise<Object>} Updated test case
   */
  async removeTag(testCaseId, tag) {
    const testCase = await this.getById(testCaseId)
    if (!testCase) {
      throw new Error('Test case not found')
    }

    testCase.tags = testCase.tags.filter(t => t !== tag)
    return await this.update(testCaseId, { tags: testCase.tags })
  }

  /**
   * Get test case statistics
   * @param {string} projectId - Project ID (optional)
   * @returns {Promise<Object>} Test case statistics
   */
  async getStatistics(projectId = null) {
    let testCases
    if (projectId) {
      testCases = await this.getByProject(projectId)
    } else {
      testCases = await this.getAll()
    }

    const statusCounts = {}
    const typeCounts = {}
    const priorityCounts = {}
    const automationCounts = {}

    testCases.forEach(testCase => {
      // Count by status
      statusCounts[testCase.status] = (statusCounts[testCase.status] || 0) + 1

      // Count by type
      typeCounts[testCase.type] = (typeCounts[testCase.type] || 0) + 1

      // Count by priority
      priorityCounts[testCase.priority] = (priorityCounts[testCase.priority] || 0) + 1

      // Count by automation status
      automationCounts[testCase.automation_status] = (automationCounts[testCase.automation_status] || 0) + 1
    })

    return {
      total: testCases.length,
      statusCounts,
      typeCounts,
      priorityCounts,
      automationCounts,
      lastUpdated: new Date()
    }
  }

  /**
   * Get all test case statistics
   * @returns {Promise<Object>} All test case statistics
   */
  async getAllStatistics() {
    return await this.getStatistics()
  }

  /**
   * Assign test case IDs to existing test cases based on creation order
   * @returns {Promise<Object>} Result of the ID assignment process
   */
  async assignTestCaseIds() {
    try {
      // Get all test cases sorted by creation date
      const allTestCases = await this.getAll()
      const testCasesWithoutId = allTestCases.filter(tc => !tc.testCaseId)
      
      if (testCasesWithoutId.length === 0) {
        return { message: 'All test cases already have IDs', updated: 0 }
      }

      // Sort by creation date
      testCasesWithoutId.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())

      // Get existing IDs to avoid conflicts
      const existingIds = allTestCases
        .map(tc => tc.testCaseId)
        .filter(id => id && id.startsWith('TC'))
        .map(id => {
          const match = id.match(/^TC(\d+)$/)
          return match ? parseInt(match[1], 10) : 0
        })
        .filter(num => !isNaN(num))

      let nextId = 1
      if (existingIds.length > 0) {
        nextId = Math.max(...existingIds) + 1
      }

      // Assign IDs to test cases without them
      const updates = []
      for (const testCase of testCasesWithoutId) {
        const testCaseId = `TC${nextId.toString().padStart(5, '0')}`
        updates.push({
          id: testCase.id,
          testCaseId: testCaseId
        })
        nextId++
      }

      // Update test cases with new IDs
      for (const update of updates) {
        await this.update(update.id, { testCaseId: update.testCaseId })
      }

      return { 
        message: `Successfully assigned IDs to ${updates.length} test cases`, 
        updated: updates.length,
        assignedIds: updates.map(u => u.testCaseId)
      }
    } catch (error) {
      console.error('Error assigning test case IDs:', error)
      throw new Error(`Failed to assign test case IDs: ${error.message}`)
    }
  }

  // Activity tracking methods
  async getActivities(testCaseId) {
    try {
      
      // Query activities collection for this test case using findWhere
      // Use entity_type and entity_id (snake_case) to match the field names used when creating activities
      const conditions = [
        { field: 'entity_type', operator: '==', value: 'test_case' },
        { field: 'entity_id', operator: '==', value: testCaseId }
      ]
      
      const activities = await this.db.query(COLLECTIONS.ACTIVITIES, conditions)
      
      return activities || []
    } catch (error) {
      console.error('Error fetching test case activities:', error)
      throw new Error(`Failed to fetch activities: ${error.message}`)
    }
  }

  async createActivity(testCaseId, activityData) {
    try {
      
      // Create activity in activities collection
      const activity = {
        id: `activity_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        user_id: activityData.created_by || activityData.user_id,
        action: activityData.type || activityData.action || 'created',
        entity_type: 'test_case',
        entity_id: testCaseId,
        project_id: activityData.project_id,
        team_id: activityData.team_id,
        description: activityData.description || activityData.title,
        timestamp: activityData.timestamp || new Date(),
        // Additional fields from activityData
        title: activityData.title,
        details: activityData.details,
        type: activityData.type
      }
      
      
      const result = await this.db.create(COLLECTIONS.ACTIVITIES, activity)
      return result
    } catch (error) {
      console.error('Error creating test case activity:', error)
      throw new Error(`Failed to create activity: ${error.message}`)
    }
  }

  async addTestRunHistory(testCaseId, testRunData) {
    try {
      
      // Get current test case - handle both display ID and database ID
      let testCase = null
      
      try {
        // First try to find by testCaseId (display ID like TC00003)
        if (testCaseId.startsWith('TC')) {
          const testCases = await this.findBy('testCaseId', testCaseId)
          if (testCases && testCases.length > 0) {
            testCase = testCases[0]
          } else {
            // Fallback to direct ID lookup
            testCase = await this.getById(testCaseId)
          }
        } else {
          // Direct ID lookup for database IDs
          testCase = await this.getById(testCaseId)
        }
      } catch (findError) {
        console.error('❌ Error finding test case by testCaseId, trying direct lookup:', findError)
        // Fallback to direct ID lookup
        testCase = await this.getById(testCaseId)
      }
      
      if (!testCase) {
        throw new Error(`Test case not found for ID: ${testCaseId}`)
      }
      
      // Initialize testRunHistory if it doesn't exist
      if (!testCase.testRunHistory) {
        testCase.testRunHistory = []
      } else {
      }
      
      // Add new test run history entry
      const historyEntry = {
        testRunId: testRunData.testRunId,
        testRunName: testRunData.testRunName,
        status: testRunData.status || 'not_started',
        executedAt: testRunData.executedAt || new Date().toISOString(),
        executedBy: testRunData.executedBy,
        notes: testRunData.notes || '',
        addedAt: new Date().toISOString()
      }
      
      // Add to history (most recent first)
      testCase.testRunHistory.unshift(historyEntry)
      
      // Update test case with new history (use database ID)
      const updatedTestCase = await this.update(testCase.id, {
        testRunHistory: testCase.testRunHistory,
        updated_at: new Date().toISOString()
      })
      
      return updatedTestCase
    } catch (error) {
      console.error('❌ Error adding test run history:', error)
      console.error('❌ Error stack:', error.stack)
      throw new Error(`Failed to add test run history: ${error.message}`)
    }
  }

  async getTestRunHistory(testCaseId) {
    try {
      
      let testCase = null
      
      // Try to find test case by testCaseId (display ID) first, then by database ID
      try {
        // First try to find by testCaseId (display ID like TC00003)
        if (testCaseId.startsWith('TC')) {
          const testCases = await this.findBy('testCaseId', testCaseId)
          if (testCases && testCases.length > 0) {
            testCase = testCases[0]
          } else {
            // Fallback to direct ID lookup
            testCase = await this.getById(testCaseId)
          }
        } else {
          // Direct ID lookup for database IDs
          testCase = await this.getById(testCaseId)
        }
      } catch (findError) {
        // Fallback to direct ID lookup
        testCase = await this.getById(testCaseId)
      }
      
      if (!testCase) {
        throw new Error('Test case not found')
      }
      
      return testCase.testRunHistory || []
    } catch (error) {
      console.error('Error getting test run history:', error)
      throw new Error(`Failed to get test run history: ${error.message}`)
    }
  }

  /**
   * Soft delete a test case
   * @param {string} testCaseId - Test case ID
   * @param {string} deletedBy - User ID who deleted the test case
   * @param {string} deletedByName - Name of user who deleted the test case
   * @returns {Promise<Object>} Updated test case
   */
  async softDelete(testCaseId, deletedBy, deletedByName = 'Unknown') {
    try {
      // Use dedicated soft-delete endpoint (bypasses update() safeguard)
      const { http } = await import('@/utils/httpClient')
      const response = await http.post(`/test_cases/${testCaseId}/soft-delete`, {
        deletedBy,
        deletedByName
      })
      return response
    } catch (error) {
      console.error('❌ Error soft deleting test case:', error)
      throw error
    }
  }

  /**
   * Soft delete a test case with pre-loaded data (optimized for bulk operations)
   * @param {string} testCaseId - Test case ID
   * @param {string} deletedBy - User ID who deleted the test case
   * @param {string} deletedByName - Name of user who deleted the test case
   * @param {Object} existingTestCase - Pre-loaded test case data (optional, will fetch if not provided)
   * @returns {Promise<Object>} Updated test case
   */
  async softDeleteWithData(testCaseId, deletedBy, deletedByName = 'Unknown', existingTestCase = null) {
    try {
      // Use provided test case data or fetch if not provided
      let testCaseData = existingTestCase
      if (!testCaseData) {
        testCaseData = await this.getById(testCaseId)
        if (!testCaseData) {
          throw new Error('Test case not found')
        }
      }

      const now = new Date()
      const activities = testCaseData.activities || []
      
      // Add deletion activity
      activities.push({
        id: Date.now() + Math.random(),
        type: 'deleted',
        title: 'Test Case Deleted',
        description: `deleted this test case`,
        user_id: deletedBy,
        user_name: deletedByName,
        timestamp: now,
        details: {
          testCaseId: testCaseData.testCaseId,
          name: testCaseData.name
        }
      })

      const updateData = {
        is_deleted: true,
        deleted_at: now,
        deleted_by: deletedBy,
        updated_at: now,
        activities: activities
      }
      
      const result = await this.update(testCaseId, updateData)
      return result
    } catch (error) {
      console.error('❌ Error soft deleting test case:', error)
      throw error
    }
  }

  /**
   * Restore a soft-deleted test case
   * @param {string} testCaseId - Test case ID
   * @param {string} restoredBy - User ID who restored the test case
   * @param {string} restoredByName - Name of user who restored the test case
   * @returns {Promise<Object>} Updated test case
   */
  async restore(testCaseId, restoredBy, restoredByName = 'Unknown') {
    try {
      // Get existing test case to access activities
      const existingTestCase = await this.getById(testCaseId)
      if (!existingTestCase) {
        throw new Error('Test case not found')
      }

      return this.restoreWithData(testCaseId, restoredBy, restoredByName, existingTestCase)
    } catch (error) {
      console.error('Error restoring test case:', error)
      throw error
    }
  }

  /**
   * Restore a test case with pre-loaded data (optimized for bulk operations)
   * @param {string} testCaseId - Test case ID
   * @param {string} restoredBy - User ID who restored the test case
   * @param {string} restoredByName - Name of user who restored the test case
   * @param {Object} existingTestCase - Pre-loaded test case data (optional, will fetch if not provided)
   * @returns {Promise<Object>} Updated test case
   */
  async restoreWithData(testCaseId, restoredBy, restoredByName = 'Unknown', existingTestCase = null) {
    try {
      // Use provided test case data or fetch if not provided
      let testCaseData = existingTestCase
      if (!testCaseData) {
        testCaseData = await this.getById(testCaseId)
        if (!testCaseData) {
          throw new Error('Test case not found')
        }
      }

      // Check if an active test case with the same name already exists in the project
      const activeTestCases = await this.getByProject(testCaseData.project_id)
      const duplicateActive = activeTestCases.find(tc => 
        tc.name.toLowerCase().trim() === testCaseData.name.toLowerCase().trim() &&
        !tc.is_deleted &&
        tc.id !== testCaseId
      )

      if (duplicateActive) {
        throw new Error(`Cannot restore test case: A test case named "${testCaseData.name}" already exists in this project. Please rename or delete the existing test case first.`)
      }

      const now = new Date()
      const activities = testCaseData.activities || []
      
      // Add restoration activity
      activities.push({
        id: Date.now() + Math.random(),
        type: 'restored',
        title: 'Test Case Restored',
        description: `restored this test case`,
        user_id: restoredBy,
        user_name: restoredByName,
        timestamp: now,
        details: {
          testCaseId: testCaseData.testCaseId,
          name: testCaseData.name
        }
      })

      const updateData = {
        is_deleted: false,
        deleted_at: null,
        deleted_by: null,
        updated_at: now,
        activities: activities
      }
      
      const result = await this.update(testCaseId, updateData)
      return result
    } catch (error) {
      console.error('Error restoring test case:', error)
      throw error
    }
  }

  /**
   * Get deleted test cases for a project
   * @param {string} projectId - Project ID
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Array of deleted test cases
   */
  async getDeletedByProject(projectId, options = {}) {
    try {
      const testCases = await this.findBy('project_id', projectId, options)
      // Filter for deleted test cases and map database field names to UI field names
      return testCases
        .filter(testCase => testCase.is_deleted === true)
        .map(testCase => ({
          ...testCase,
          folderId: testCase.folder_id || testCase.folderId,
          projectId: testCase.project_id
        }))
    } catch (error) {
      console.error('Error getting deleted test cases:', error)
      throw error
    }
  }

  /**
   * Permanently delete a test case (hard delete)
   * @param {string} testCaseId - Test case ID
   * @returns {Promise<Object>} Deletion result
   */
  async permanentDelete(testCaseId) {
    try {
      const result = await this.delete(testCaseId)
      return result
    } catch (error) {
      console.error('Error permanently deleting test case:', error)
      throw error
    }
  }

  /**
   * Link a bug to a test case
   * @param {string} testCaseId - Test case ID (TC00001) or database ID
   * @param {string} bugId - Bug ID (B00001)
   * @param {string} userId - User ID who linked the bug
   * @returns {Promise<Object>} Updated test case
   */
  async linkBug(testCaseId, bugId, userId = null) {
    try {
      // Find test case by testCaseId or database ID
      let testCase = null
      if (testCaseId.startsWith('TC')) {
        const testCases = await this.findBy('testCaseId', testCaseId)
        testCase = testCases && testCases.length > 0 ? testCases[0] : null
      } else {
        testCase = await this.getById(testCaseId)
      }

      if (!testCase) {
        throw new Error('Test case not found')
      }

      // Get current linked bugs
      const linkedBugs = testCase.linked_bugs || []
      
      // Check if bug is already linked
      if (linkedBugs.includes(bugId)) {
        return testCase
      }

      // Add bug to linked_bugs array
      linkedBugs.push(bugId)

      // Add activity
      const activities = testCase.activities || []
      if (userId) {
        activities.push({
          id: Date.now() + Math.random(),
          type: 'bug_linked',
          title: 'Bug Linked',
          description: `Bug ${bugId} was linked to this test case`,
          user_id: userId,
          timestamp: new Date(),
          details: {
            bugId: bugId
          }
        })
      }

      // Update test case
      const updateData = {
        linked_bugs: linkedBugs,
        activities: activities
      }

      return await this.update(testCase.id, updateData)
    } catch (error) {
      console.error('Error linking bug to test case:', error)
      throw error
    }
  }

  /**
   * Unlink a bug from a test case
   * @param {string} testCaseId - Test case ID (TC00001) or database ID
   * @param {string} bugId - Bug ID (B00001)
   * @param {string} userId - User ID who unlinked the bug
   * @returns {Promise<Object>} Updated test case
   */
  async unlinkBug(testCaseId, bugId, userId = null) {
    try {
      // Find test case by testCaseId or database ID
      let testCase = null
      if (testCaseId.startsWith('TC')) {
        const testCases = await this.findBy('testCaseId', testCaseId)
        testCase = testCases && testCases.length > 0 ? testCases[0] : null
      } else {
        testCase = await this.getById(testCaseId)
      }

      if (!testCase) {
        throw new Error('Test case not found')
      }

      // Get current linked bugs
      const linkedBugs = testCase.linked_bugs || []
      
      // Remove bug from linked_bugs array
      const updatedLinkedBugs = linkedBugs.filter(id => id !== bugId)

      // Add activity
      const activities = testCase.activities || []
      if (userId) {
        activities.push({
          id: Date.now() + Math.random(),
          type: 'bug_unlinked',
          title: 'Bug Unlinked',
          description: `Bug ${bugId} was unlinked from this test case`,
          user_id: userId,
          timestamp: new Date(),
          details: {
            bugId: bugId
          }
        })
      }

      // Update test case
      const updateData = {
        linked_bugs: updatedLinkedBugs,
        activities: activities
      }

      return await this.update(testCase.id, updateData)
    } catch (error) {
      console.error('Error unlinking bug from test case:', error)
      throw error
    }
  }

  /**
   * Get bugs linked to a test case
   * @param {string} testCaseId - Test case ID (TC00001) or database ID
   * @returns {Promise<Array>} Array of bug IDs
   */
  async getLinkedBugs(testCaseId) {
    try {
      // Find test case by testCaseId or database ID
      let testCase = null
      if (testCaseId.startsWith('TC')) {
        const testCases = await this.findBy('testCaseId', testCaseId)
        testCase = testCases && testCases.length > 0 ? testCases[0] : null
      } else {
        testCase = await this.getById(testCaseId)
      }

      if (!testCase) {
        return []
      }

      return testCase.linked_bugs || []
    } catch (error) {
      console.error('Error getting linked bugs:', error)
      return []
    }
  }

  /**
   * Batch soft delete multiple test cases
   * @param {Array<string>} testCaseIds - Array of test case IDs
   * @param {string} deletedBy - User ID who deleted the test cases
   * @param {string} deletedByName - Name of user who deleted the test cases
   * @returns {Promise<Object>} Result with success and failed counts
   */
  async batchSoftDelete(testCaseIds, deletedBy, deletedByName = 'Unknown') {
    try {
      if (!testCaseIds || testCaseIds.length === 0) {
        return { success: 0, failed: 0 }
      }

      const { http } = await import('@/utils/httpClient')
      const response = await http.post('/test_cases/batch-soft-delete', {
        testCaseIds,
        deletedBy,
        deletedByName
      })
      
      return response
    } catch (error) {
      console.error('❌ Error batch deleting test cases:', error)
      throw error
    }
  }

  /**
   * Batch restore multiple test cases
   * @param {Array<string>} testCaseIds - Array of test case IDs
   * @param {string} restoredBy - User ID who restored the test cases
   * @param {string} restoredByName - Name of user who restored the test cases
   * @returns {Promise<Object>} Result with success and failed counts
   */
  async batchRestore(testCaseIds, restoredBy, restoredByName = 'Unknown') {
    try {
      if (!testCaseIds || testCaseIds.length === 0) {
        return { success: 0, failed: 0 }
      }

      const { http } = await import('@/utils/httpClient')
      const response = await http.post('/test_cases/batch-restore', {
        testCaseIds,
        restoredBy,
        restoredByName
      })
      
      return response
    } catch (error) {
      console.error('❌ Error batch restoring test cases:', error)
      throw error
    }
  }

  /**
   * Batch update multiple test cases
   * @param {Array<string>} testCaseIds - Array of test case IDs
   * @param {Object} updateData - Data to update
   * @returns {Promise<Object>} Result with success and failed counts
   */
  async batchUpdate(testCaseIds, updateData) {
    try {
      if (!testCaseIds || testCaseIds.length === 0) {
        return { success: 0, failed: 0 }
      }

      const { http } = await import('@/utils/httpClient')
      const response = await http.post('/test_cases/batch-update', {
        testCaseIds,
        updateData
      })
      
      return response
    } catch (error) {
      console.error('❌ Error batch updating test cases:', error)
      throw error
    }
  }

  /**
   * Bulk create multiple test cases in a single operation
   * Uses MongoDB insertMany for optimal performance
   * @param {Array<Object>} testCases - Array of test case data objects
   * @returns {Promise<Array>} Array of created test cases
   */
  async bulkCreate(testCases) {
    try {
      if (!testCases || testCases.length === 0) {
        return []
      }

      const { http } = await import('@/utils/httpClient')
      const response = await http.post('/test_cases/bulk-create', {
        testCases
      })
      
      return response
    } catch (error) {
      console.error('❌ Error bulk creating test cases:', error)
      throw error
    }
  }
}

export const testCaseService = new TestCaseService()